import { GridSortItem } from "@mui/x-data-grid";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { ProviderControllerApi } from "../../../services";
import {
  DGT_Pagination,
  asPaginatedContent,
  getConfigObject,
  getPageIndexWhileSearch,
  getPageSize,
  useBaseContent,
} from "../../../utils/utils";
import { UserCardData } from "../../../types/user-types";

export interface UseProvidersListProps {
  pagination: DGT_Pagination;
  gridSorting?: GridSortItem[];
  searchKey?: string;
  status?: boolean | undefined;
  state?: string | boolean | null;
  xTENANTID?: string;
  locationUuid?: string;
  speciality?: string;
  isReferral?: boolean; // This can be passed as a parameter if needed
  enabled?: boolean;
  archive?: boolean;
}

/**
 * @author Shrinivas Mandhare
 */
export function useProvidersList({
  gridSorting,
  pagination,
  searchKey,
  xTENANTID,
  isReferral = false, // This can be passed as a parameter if needed
  state,
  speciality,
  status,
  locationUuid,
  enabled = true,
  archive,
}: UseProvidersListProps): useBaseContent<UserCardData> {
  const prevPage = useRef<number>(pagination.page);

  const pageIndex = getPageIndexWhileSearch({
    pagination,
    searchKey,
    prevPage,
  });
  const pageSize = getPageSize(searchKey, pagination);

  const sortData = gridSorting?.[0];

  return useQuery({
    queryKey: [
      "providers-List",
      pageSize,
      pageIndex,
      searchKey,
      xTENANTID,
      state,
      isReferral,
      status,
      gridSorting,
      speciality,
      locationUuid,
      archive,
    ],
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    enabled: enabled,
    retry: (count) => count < 3,
    queryFn: async () => {
      return new ProviderControllerApi(getConfigObject())
        .getAllProviders(
          pageIndex, //page
          pageSize, //size
          sortData?.field ?? undefined, // sortBy
          sortData?.sort ?? undefined, // sortDirection
          status as boolean, //status
          archive, // archive
          isReferral, // isReferral
          state as string, // state
          undefined, //role
          speciality, //speciality,
          locationUuid, //locationUuid
          searchKey ? searchKey : undefined, //searchString
          xTENANTID //xTENANTID
        )
        .then((res) => asPaginatedContent<UserCardData>(res?.data));
    },
  });
}
